/**
 * @quotech/ui - Access Control Settings Component
 *
 * Settings page for managing page/module access permissions.
 *
 * @path packages/ui/src/components/AccessControlSettings.tsx
 */

import { useState, useMemo } from 'react'
import { Shield, Search, ChevronDown, Building2, CheckCircle2, XCircle, AlertCircle, Filter, RotateCcw } from 'lucide-react'
import { cn } from '../lib/utils'
import type { HubId, NavAccessLevel } from './types'
import { HUB_REGISTRY } from '../lib/hubRegistry'
import { HubSection } from './access-control-settings/HubSection'
import { getRoleIcon, getRoleColor } from './access-control-settings/PageAccessRow'

interface AccessControlSettingsProps {
  userRole: NavAccessLevel
  canManage: boolean
  pageOverrides: Record<string, boolean>
  onPageAccessChange?: (route: string, allowed: boolean) => void
  onSave?: (overrides: Record<string, boolean>) => void
  onReset?: () => void
  className?: string
}

export function AccessControlSettings({
  userRole, canManage, pageOverrides, onPageAccessChange, onSave, onReset, className,
}: AccessControlSettingsProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterRole, setFilterRole] = useState<NavAccessLevel | 'all'>('all')
  const [localOverrides, setLocalOverrides] = useState(pageOverrides)
  const [hasChanges, setHasChanges] = useState(false)

  const hubPages = useMemo(() => {
    const result: Record<HubId, Array<{ route: string; label: string; accessLevel: NavAccessLevel; premiumOnly?: boolean }>> = {} as any
    Object.values(HUB_REGISTRY).forEach((hub) => {
      result[hub.id] = (hub.pages || []).map((page) => ({ route: page.href, label: page.label, accessLevel: 'member' as NavAccessLevel, premiumOnly: false }))
    })
    return result
  }, [])

  const filteredHubs = useMemo(() => {
    return Object.values(HUB_REGISTRY)
      .filter((hub) => {
        const pages = hubPages[hub.id] || []
        return pages.some((page) => {
          if (searchQuery) { const q = searchQuery.toLowerCase(); if (!page.label.toLowerCase().includes(q) && !page.route.toLowerCase().includes(q)) return false }
          if (filterRole !== 'all' && page.accessLevel !== filterRole) return false
          return true
        })
      })
      .map((hub) => ({
        hub,
        pages: (hubPages[hub.id] || []).filter((page) => {
          if (searchQuery) { const q = searchQuery.toLowerCase(); if (!page.label.toLowerCase().includes(q) && !page.route.toLowerCase().includes(q)) return false }
          if (filterRole !== 'all' && page.accessLevel !== filterRole) return false
          return true
        }),
      }))
  }, [hubPages, searchQuery, filterRole])

  const handlePageToggle = (route: string) => {
    const currentValue = localOverrides[route] !== false
    const newOverrides = { ...localOverrides, [route]: !currentValue }
    setLocalOverrides(newOverrides)
    setHasChanges(true)
    onPageAccessChange?.(route, !currentValue)
  }

  const handleSave = () => { onSave?.(localOverrides); setHasChanges(false) }
  const handleReset = () => { setLocalOverrides({}); setHasChanges(false); onReset?.() }

  const overallStats = useMemo(() => {
    let total = 0, blocked = 0
    Object.values(hubPages).forEach((pages) => pages.forEach((page) => { total++; if (localOverrides[page.route] === false) blocked++ }))
    return { total, allowed: total - blocked, blocked }
  }, [hubPages, localOverrides])

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" /> Access Control
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage page-level access permissions for users</p>
        </div>
        <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg', getRoleColor(userRole))}>
          {(() => { const Icon = getRoleIcon(userRole); return <Icon className="w-4 h-4" /> })()}
          <span className="text-sm font-medium capitalize">Your role: {userRole}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { icon: Building2, value: overallStats.total, label: 'Total Pages', color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-700' },
          { icon: CheckCircle2, value: overallStats.allowed, label: 'Accessible', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
          { icon: XCircle, value: overallStats.blocked, label: 'Restricted', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', stat.bg)}><stat.icon className={cn('w-5 h-5', stat.color)} /></div>
              <div><p className={cn('text-2xl font-bold', stat.color)}>{stat.value}</p><p className="text-xs text-slate-500 dark:text-slate-400">{stat.label}</p></div>
            </div>
          </div>
        ))}
      </div>

      {/* Search and filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search pages..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
        </div>
        <div className="relative">
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value as NavAccessLevel | 'all')} className="appearance-none pl-9 pr-8 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
            <option value="all">All Roles</option><option value="public">Public</option><option value="member">Member</option><option value="admin">Admin</option><option value="owner">Owner</option>
          </select>
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        </div>
        {hasChanges && <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"><RotateCcw className="w-4 h-4" /> Reset</button>}
        {canManage && hasChanges && <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors"><CheckCircle2 className="w-4 h-4" /> Save Changes</button>}
      </div>

      {!canManage && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div><p className="text-sm font-medium text-amber-800 dark:text-amber-200">View-only access</p><p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">You can view access settings but cannot make changes. Contact an administrator to modify permissions.</p></div>
        </div>
      )}

      <div className="space-y-4">
        {filteredHubs.map(({ hub, pages }) => (
          <HubSection key={hub.id} hub={hub} pages={pages} pageOverrides={localOverrides} canManage={canManage} onPageToggle={handlePageToggle} />
        ))}
        {filteredHubs.length === 0 && (
          <div className="text-center py-12"><Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" /><p className="text-slate-500 dark:text-slate-400">No pages found matching your search</p></div>
        )}
      </div>
    </div>
  )
}

export default AccessControlSettings
